import { ComponentProps } from '../App';
import {
  ComponentConfig,
  ComponentStyleProps,
  ContainerConfig,
} from '../pages/types';

// List of properties that should be treated as styles
const STYLE_PROPERTIES = [
  'background',
  'backgroundImage',
  'color',
  'width',
  'height',
  'padding',
  'margin',
  'borderRadius',
  'textAlign',
];

/**
 * Cleans component data by properly separating props and styles
 */
export function cleanComponentData(component: any): ComponentConfig {
  if (!component) {
    return { type: 'Unknown', props: {}, styles: {} };
  }

  const type = component.type || 'Unknown';
  const id = component.id; // Preserve the id field
  const mixedProps = component.props || {};
  const existingStyles = component.styles || {};

  // Initialize clean objects
  const contentProps: ComponentProps = {};
  const styles: Record<string, any> = { ...existingStyles };

  // Extract style properties from props into styles
  Object.entries(mixedProps).forEach(([key, value]) => {
    if (STYLE_PROPERTIES.includes(key)) {
      styles[key] = value;
    } else {
      contentProps[key] = value;
    }
  });

  // Handle special case for sx props in MUI
  if (mixedProps.sx) {
    Object.assign(styles, mixedProps.sx);
    delete contentProps.sx;
  }

  // Handle container configuration if present
  let containerConfig: ContainerConfig | undefined = undefined;

  if (mixedProps.container) {
    containerConfig = {
      columns: mixedProps.container.columns || 2,
      spacing: mixedProps.container.spacing || 2,
      maxWidth: mixedProps.container.maxWidth,
      minColumns: mixedProps.container.minColumns,
      items: Array.isArray(mixedProps.container.items)
        ? mixedProps.container.items.map((item: any) =>
            cleanComponentData(item)
          )
        : [],
    };
  }

  // Return cleaned component with properly separated props and styles, and preserved id
  const result: ComponentConfig = {
    type,
    props: contentProps,
    styles: styles as ComponentStyleProps,
    container: containerConfig,
  };

  // Add id if it exists
  if (id) {
    result.id = id;
  }

  return result;
}

/**
 * Creates a normalized component config from any input
 */
export function normalizeComponent(input: any): ComponentConfig {
  // If it's already in the right format, just clean it
  if (input && typeof input === 'object' && 'type' in input) {
    return cleanComponentData(input);
  }

  // Default empty component
  return {
    type: 'Unknown',
    props: {},
    styles: {},
  };
}

export function getMainDomain(): string {
  const hostname = window.location.hostname;
  const match = hostname.match(/([^.]+\.[^.]+)$/);
  return match ? match[1] : hostname;
}
